import React, { useState, useRef } from 'react';
import {
  Modal,
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Backdrop,
  Fade,
  Stack,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../hooks/useAppDispatch';
import {
  BusinessProfileContacts,
  createBusinessProfileContact,
} from '../../slices/business-profile/businessProfileSlice';
import ContactNameInput from '../form-inputs/contact-name-input/ContactNameInput';
import EmailAddressInput from '../form-inputs/email-address-input/EmailAddresInput';
import PhoneInput from '../form-inputs/phone-input/PhoneInput';
import AddressInput from '../form-inputs/address-input/AddressInput';
import PositionInput from '../form-inputs/position-input/PositionInput';
import AuthorizedSignCheckbox from '../authorized-sign-checkbox/AuthorizedSignCheckbox';
import ShareholderCheckbox from '../share-holder-checkbox/ShareHolderCheckbox';
import HiringManagerCheckbox from '../hiring-manager-checkbox/HiringManagerCheckBox';
import LegalRepresentationCheckbox from '../legal-representation-checkbox/LegalRepresentationCheckbox';
import styles from './CreateCustomerContactModal.module.scss';
import { useAppSelector } from '../hooks/useAppSelector';
import { NWClient } from '../../client/NWClient';
import { fetchCustomers } from '../../slices/customers/customersSlice';
import { Customer } from '../../models/customer.model';
import { toast } from 'react-toastify';
import { Navigate, useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';

interface CreateCustomerContactModalProps {
  open: boolean;
  onClose: () => void;
  customer: Customer;
  companyId: number;
  onSuccessfulSubmit?: () => void;
}

export default function CreateCustomerContactModal({
  open,
  onClose,
  onSuccessfulSubmit,
  customer,
  companyId,
}: CreateCustomerContactModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLDivElement | null>(null);
  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);

  const [formData, setFormData] = useState({
    detail_type: '',
    detail: '',
    customer_id: customer.id,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleChange =
    <K extends keyof typeof formData>(field: K) =>
    (value: (typeof formData)[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleCreate = async () => {
    setIsSubmitting(true);

    try {
      // Check if the detail_type already exists
      const isDuplicate = customer.contact_details.some(
        (contact) => contact.detail_type === formData.detail_type
      );

      if (isDuplicate) {
        toast.error('This contact type already exists for the customer.');
        setIsSubmitting(false);
        return;
      }

      // Proceed with API call if no duplicate
      NWClient.post('customer-contacts', formData, true).then(() => {
        dispatch(fetchCustomers({ company_id: companyId }));
      });
      onSuccessfulSubmit();
    } catch (error) {
      console.error('Failed to create customer contact:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submission Button
  const submissionButtonSx = {
    fontFamily: "'Regular Inter', sans-serif",
    boxShadow: 'none',
    fontSize: '0.75rem',
    fontWeight: 400,
    minWidth: '100px',
    height: '44px',
    borderRadius: '8px',
    textTransform: 'none',
    backgroundColor: '#ecdfff', // $purple-light
    color: '#6414db', // $brand-purple
    '&:hover': {
      color: '#fff', // $base-white
      backgroundColor: '#b17dff', // $purple-light-2
    },
  };

  // Closing Button
  const closingButtonSx = {
    fontFamily: "'Regular Inter', sans-serif",
    fontSize: '0.75rem',
    fontWeight: 400,
    minWidth: '100px',
    height: '44px',
    borderRadius: '8px',
    textTransform: 'capitalize',
    backgroundColor: '#fff', // $base-white
    color: '#000000', // $base-black
    '&:hover': {
      backgroundColor: '#ededed', // $gray-variant
    },
  };

  const contactMethodOptions = [
    { key: 'phone_number', label: 'Phone Number' },
    { key: 'email_address', label: 'Email Address' },
    { key: 'social_media', label: 'Social Media' },
  ];

  return (
    <Modal open={open} onClose={onClose} className={'action-modal'} closeAfterTransition>
      <Grid container className={styles.modal_container}>
        <Grid item xs={12} sm={8} md={6} lg={4}>
          <Card ref={formRef}>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant='h6' gutterBottom>
                {t('labels.preferredContactMethod')}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Select
                    fullWidth
                    value={formData.detail_type ?? ''}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleChange('detail_type')(e.target.value);
                    }}
                    displayEmpty
                    inputProps={{ 'aria-label': t('labels.preferredContactMethod') }}
                  >
                    <MenuItem value='' disabled>
                      Select contact method
                    </MenuItem>
                    {contactMethodOptions.map((option) => (
                      <MenuItem
                        key={option.key}
                        value={option.key}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  {formData.detail_type !== '' && (
                    <TextField
                      fullWidth
                      className={styles.textfield_animation}
                      type='text'
                      label={
                        contactMethodOptions.find((option) => option.key === formData.detail_type)
                          ?.label || ''
                      }
                      value={formData.detail}
                      onChange={(e) => handleChange('detail')(e.target.value)}
                      variant='outlined'
                    />
                  )}
                </Grid>
              </Grid>
              <Stack direction='row' gap={'8px'} justifyContent='flex-end' marginTop={'16px'}>
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic btn-cancel'
                  onClick={onClose}
                >
                  {t('buttons.close')}
                </Button>
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic'
                  onClick={handleCreate}
                >
                  {t('buttons.create')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
}
